import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { ConfigProvider } from "antd";
import { BrowserRouter } from "react-router-dom";
import antDesignTokens from "@ahaslides-product/plugins-design";
import { App } from "./App";
import { initSentry } from "./sentry";
import { AppErrorBoundary } from "./app/AppErrorBoundary";
import { initializeTeamApp } from "./host/teamAppZoid";
import { installDevHostShim } from "./host/devHostShim";
import "./i18n";
import { I18nLanguageBridge } from "./i18n/I18nLanguageBridge";
import "./tokens.css";
import "./main.css";

// Build-time gate for the dev-only host-identity overlay. This must be an
// inline `import.meta.env` literal (NOT isDevHostEnabled()) so Vite folds it
// to a constant: in production it becomes `false`, the ternary collapses to
// null, and Rollup drops the DevHostPanel dynamic import — the overlay and its
// AntD-form code never ship. A function call wouldn't be const-foldable, so the
// chunk would still be emitted. This is the bootstrap entry, not a component,
// so the "don't read import.meta.env in components" rule doesn't apply.
const DEV_HOST_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEV_HOST === "true";

const DevHostPanel = DEV_HOST_ENABLED
  ? lazy(() =>
      import("./host/DevHostPanel").then((m) => ({ default: m.DevHostPanel })),
    )
  : null;

// Before anything else so Sentry can catch errors thrown during host/init too.
// No-ops without VITE_SENTRY_DSN — see ./sentry.
initSentry();

initializeTeamApp();

// Bootstrap is async so the dev-host shim can enrich the mock identity from
// GET /api/profile before the first render (no-op + resolves immediately in
// production, where the shim early-returns). Consumers read xprops once at
// mount, so the identity must be settled before createRoot.
async function bootstrap() {
  await installDevHostShim();

  const container = document.getElementById("root");
  if (!container) {
    throw new Error("#root element not found");
  }

  createRoot(container).render(
    <StrictMode>
    {/* cssVar exposes the theme tokens as `--ant-color-*` CSS variables
        so app-level stylesheets (main.css, *.module.css) can reference
        the same design tokens without hardcoding hexes.

        colorLink override: AntD's algorithm derives colorLink from
        colorInfo when only seed tokens are provided — that paints
        Typography.Link, anchors, and other link affordances with the
        info blue (#9BB3E9), which clashes with the brand. Pointing
        colorLink at colorPrimary makes the whole link palette (and the
        colorLinkHover / colorLinkActive variants AntD derives
        downstream) track the primary purple. */}
    <ConfigProvider
      theme={{
        token: {
          ...antDesignTokens,
          // antDesignTokens is typed `string | number` per field (because
          // the seed token map mixes color strings with numeric sizes),
          // so narrow here — colorPrimary is always a hex string.
          colorLink: antDesignTokens.colorPrimary as string,
        },
        cssVar: {},
        hashed: false,
      }}
    >
      <BrowserRouter>
        <I18nLanguageBridge />
        <AppErrorBoundary>
          <App />
        </AppErrorBoundary>
      </BrowserRouter>
      {/* Dev-only host-identity overlay, code-split behind a build-time gate so
          it's absent from production bundles (see DEV_HOST_ENABLED above). */}
      {DevHostPanel && (
        <Suspense fallback={null}>
          <DevHostPanel />
        </Suspense>
      )}
    </ConfigProvider>
  </StrictMode>,
  );
}

void bootstrap();
